import { Eye, ShieldCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import Logo from "@/components/Logo";
import { money, formatDate } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import {
  labels as disclosureLabels,
  isAllowance,
  isTm,
  deriveDisclosureTotals,
} from "@/lib/disclosure";

// Renders a read-only customer-side preview of a Proposal or Contract using data
// already loaded on the contractor's detail page. Pure render — no API calls, no
// LLM calls, no mutation of saved data. The document's saved `language` field is
// respected by reading the customer-facing strings exactly as they were written
// at generation time (snapshot.disclaimer, contract_terms, etc.).
const previewLabel = {
  en: { kind_proposal: "Proposal", kind_contract: "Contract", agreement_with: "Agreement with" },
  es: { kind_proposal: "Propuesta", kind_contract: "Contrato", agreement_with: "Contrato con" },
};

// Pure helper that turns the in-memory proposal/contract doc into a plain-text
// version of the customer preview, respecting the doc's saved language. No
// network, no mutation — used only for clipboard copy.
const toPlainText = (kind, doc) => {
  const snap = doc.snapshot || {};
  const lang = doc.language || snap.language || "en";
  const company = snap.company || {};
  const customer = snap.customer || {};
  const totals = snap.totals || {};
  const lines = [];
  const push = (s = "") => lines.push(s);
  const section = (label) => { push(""); push(label.toUpperCase()); };

  if (kind === "proposal") {
    push(`${(lang === "es" ? "PROPUESTA" : "PROPOSAL")} — ${customer.name || ""}`);
    if (customer.address) push(customer.address);
    if (company.name) push(`${lang === "es" ? "De" : "From"}: ${company.name}`);
    push(`${lang === "es" ? "Emitida" : "Issued"}: ${formatDate(doc.created_at, lang)}`);
    section(lang === "es" ? "Alcance del trabajo" : "Scope of work");
    push(snap.scope || "—");
    if (doc.included_items?.length) {
      section(lang === "es" ? "Incluido" : "Included");
      doc.included_items.forEach((x) => push(`- ${x}`));
    }
    if (doc.excluded_items?.length) {
      section(lang === "es" ? "Excluido" : "Excluded");
      doc.excluded_items.forEach((x) => push(`- ${x}`));
    }
    section(lang === "es" ? "Precios" : "Pricing");
    (snap.line_items || []).forEach((l) => {
      push(`${l.name || "—"} — ${lang === "es" ? "Cant." : "Qty"} ${l.quantity} — ${money(l.total)}`);
    });
    push(`Subtotal: ${money(totals.subtotal)}`);
    push(`${lang === "es" ? "Impuestos" : "Tax"}: ${money(totals.tax)}`);
    push(`Total: ${money(totals.total)}`);
    push(`${lang === "es" ? "Depósito" : "Deposit"}: ${money(totals.deposit)}`);
    section(lang === "es" ? "Calendario de pagos" : "Payment schedule");
    push(doc.payment_schedule || "—");
    section(lang === "es" ? "Cronograma" : "Timeline");
    push(doc.timeline || "—");
    section(lang === "es" ? "Próximos pasos" : "Next steps");
    push(doc.next_steps || "—");
    if (snap.disclaimer) { push(""); push(snap.disclaimer); }
  } else {
    const L = previewLabel[lang] || previewLabel.en;
    push(`${L.kind_contract.toUpperCase()} — ${L.agreement_with} ${customer.name || ""}`);
    if (customer.address) push(customer.address);
    if (company.name) push(`${lang === "es" ? "De" : "From"}: ${company.name}`);
    push(`${lang === "es" ? "Emitido" : "Issued"}: ${formatDate(doc.created_at, lang)}`);
    if (snap.scope) { section(lang === "es" ? "Alcance aprobado" : "Approved scope"); push(snap.scope); }
    if (doc.contract_terms) { section(lang === "es" ? "Términos del contrato" : "Contract terms"); push(doc.contract_terms); }
    if (doc.change_order_language) { section(lang === "es" ? "Órdenes de cambio" : "Change orders"); push(doc.change_order_language); }
    if (doc.customer_responsibilities) { section(lang === "es" ? "Responsabilidades del cliente" : "Customer responsibilities"); push(doc.customer_responsibilities); }
    if (doc.exclusions) { section(lang === "es" ? "Exclusiones" : "Exclusions"); push(doc.exclusions); }
    section(lang === "es" ? "Precio del contrato" : "Contract price");
    push(money(totals.total));
    push(`${lang === "es" ? "Depósito a pagar" : "Deposit due"}: ${money(totals.deposit)} · ${lang === "es" ? "Saldo" : "Balance"}: ${money(totals.balance_due)}`);
    if (doc.legal_disclaimer) {
      section("Legal disclaimer · Aviso legal");
      push(doc.legal_disclaimer);
    }
  }
  return lines.join("\n");
};

const Block = ({ title, body }) =>
  body ? (
    <div>
      <div className="overline mb-1">{title}</div>
      <p className="leading-relaxed whitespace-pre-wrap">{body}</p>
    </div>
  ) : null;

// Phase 2 Step 4: `DISCLOSURE`, `isAllowance`, `isTm`, and the disclosure
// total derivation now come from the shared `/lib/disclosure.js` helper so
// the in-app preview stays in lockstep with PublicDocs + PDF rendering.

const AllowanceBadge = ({ lang = "en" }) => (
  <span data-testid="allowance-badge" className="ml-2 inline-block align-middle px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-wider uppercase bg-[var(--jc-bg)] border border-[var(--jc-border-strong)] text-[var(--jc-text-soft)]">
    {disclosureLabels(lang).allowance_badge}
  </span>
);

const ProposalPreview = ({ doc }) => {
  const snap = doc.snapshot || {};
  const lang = doc.language || snap.language || "en";
  const L = previewLabel[lang] || previewLabel.en;
  const D = disclosureLabels(lang);
  const company = snap.company || {};
  const customer = snap.customer || {};
  const totals = snap.totals || {};
  const { tmEstimate } = deriveDisclosureTotals(snap);

  return (
    <article className="bg-white border border-[var(--jc-border)] rounded-sm p-5 lg:p-8">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between border-b border-[var(--jc-border)] pb-5 gap-4">
        <div>
          <div className="overline">{L.kind_proposal}</div>
          <h1 className="font-display text-2xl mt-1">{customer.name}</h1>
          <div className="text-sm text-[var(--jc-text-soft)] mt-1">{customer.address}</div>
        </div>
        <div className="text-sm text-[var(--jc-text-soft)] sm:text-right">
          <div>{lang === "es" ? "De: " : "From: "}<span className="font-semibold text-[var(--jc-text)]">{company.name}</span></div>
          <div>{lang === "es" ? "Emitida: " : "Issued: "}{formatDate(doc.created_at, lang)}</div>
        </div>
      </header>

      <section className="mt-5 text-sm">
        <div className="overline mb-2">{lang === "es" ? "Alcance del trabajo" : "Scope of work"}</div>
        <p className="leading-relaxed">{snap.scope}</p>
        {doc.included_items?.length > 0 && (
          <>
            <div className="overline mt-4 mb-2">{lang === "es" ? "Incluido" : "Included"}</div>
            <ul className="list-disc pl-6 space-y-1">
              {doc.included_items.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </>
        )}
        {doc.excluded_items?.length > 0 && (
          <>
            <div className="overline mt-4 mb-2">{lang === "es" ? "Excluido" : "Excluded"}</div>
            <ul className="list-disc pl-6 space-y-1">
              {doc.excluded_items.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </>
        )}
      </section>

      <section className="mt-5 overflow-x-auto">
        <div className="overline mb-2">{lang === "es" ? "Precios" : "Pricing"}</div>
        <table className="w-full text-sm border border-[var(--jc-border)]">
          <thead className="bg-[var(--jc-bg)]">
            <tr>
              <th className="text-left px-3 py-2">{lang === "es" ? "Artículo" : "Item"}</th>
              <th className="text-right px-3 py-2">{lang === "es" ? "Cant." : "Qty"}</th>
              <th className="text-right px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--jc-border)]">
            {(snap.line_items || []).filter((l) => !isTm(l)).map((l, i) => (
              <tr key={i}>
                <td className="px-3 py-2">
                  <div className="font-semibold">{l.name || "—"}{isAllowance(l) && <AllowanceBadge lang={lang} />}</div>
                  <div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td>
                <td className="px-3 py-2 text-right font-mono-jc">{money(l.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-[var(--jc-bg)] text-sm">
            <tr><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">Subtotal</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(totals.subtotal)}</td></tr>
            <tr><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{lang === "es" ? "Impuestos" : "Tax"}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(totals.tax)}</td></tr>
            <tr><td colSpan={2} className="px-3 py-2 text-right font-semibold">Total</td><td className="px-3 py-2 text-right font-mono-jc font-bold">{money(totals.total)}</td></tr>
            <tr><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{lang === "es" ? "Depósito" : "Deposit"}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(totals.deposit)}</td></tr>
            {tmEstimate > 0 && (
              <tr data-testid="tm-reserve-summary"><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)] italic">{D.tm_reserve_label} <span className="text-[10px] uppercase tracking-wider">· {D.tm_reserve_note}</span></td><td className="px-3 py-1.5 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(tmEstimate)}</td></tr>
            )}
          </tfoot>
        </table>
        {(snap.line_items || []).some(isAllowance) && (
          <p data-testid="allowance-legend" className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">
            <span className="font-semibold">{D.allowance_badge}:</span> {D.allowance_legend}
          </p>
        )}
      </section>

      {(snap.line_items || []).some(isTm) && (
        <section data-testid="tm-section" className="mt-5 overflow-x-auto">
          <div className="overline mb-2">{D.tm_section_title}</div>
          <table className="w-full text-sm border border-[var(--jc-border)]">
            <thead className="bg-[var(--jc-bg)]"><tr><th className="text-left px-3 py-2">{D.item}</th><th className="text-right px-3 py-2">{D.qty}</th><th className="text-right px-3 py-2">{D.est_total}</th></tr></thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {(snap.line_items || []).filter(isTm).map((l, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{l.name || "—"}</div>
                    <div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td>
                  <td className="px-3 py-2 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">{D.tm_legend}</p>
        </section>
      )}

      <section className="grid sm:grid-cols-2 gap-5 mt-5 text-sm">
        <div><div className="overline mb-1">{lang === "es" ? "Calendario de pagos" : "Payment schedule"}</div><p>{doc.payment_schedule || "—"}</p></div>
        <div><div className="overline mb-1">{lang === "es" ? "Cronograma" : "Timeline"}</div><p>{doc.timeline || "—"}</p></div>
        <div className="sm:col-span-2"><div className="overline mb-1">{lang === "es" ? "Próximos pasos" : "Next steps"}</div><p>{doc.next_steps || "—"}</p></div>
      </section>

      <footer className="mt-6 pt-5 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]">
        {snap.disclaimer}
      </footer>
    </article>
  );
};

const ContractPreview = ({ doc }) => {
  const snap = doc.snapshot || {};
  const lang = doc.language || snap.language || "en";
  const L = previewLabel[lang] || previewLabel.en;
  const D = disclosureLabels(lang);
  const company = snap.company || {};
  const customer = snap.customer || {};
  const totals = snap.totals || {};
  const { tmEstimate, allowanceTotal } = deriveDisclosureTotals(snap);

  return (
    <article className="bg-white border border-[var(--jc-border)] rounded-sm p-5 lg:p-8">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between border-b border-[var(--jc-border)] pb-5 gap-4">
        <div>
          <div className="overline">{L.kind_contract}</div>
          <h1 className="font-display text-2xl mt-1">{L.agreement_with} {customer.name}</h1>
          <div className="text-sm text-[var(--jc-text-soft)] mt-1">{customer.address}</div>
        </div>
        <div className="text-sm text-[var(--jc-text-soft)] sm:text-right">
          <div>{lang === "es" ? "De: " : "From: "}<span className="font-semibold text-[var(--jc-text)]">{company.name}</span></div>
          <div>{lang === "es" ? "Emitido: " : "Issued: "}{formatDate(doc.created_at, lang)}</div>
        </div>
      </header>

      <section className="mt-5 text-sm space-y-4">
        <Block title={lang === "es" ? "Alcance aprobado" : "Approved scope"} body={snap.scope} />
        <Block title={lang === "es" ? "Términos del contrato" : "Contract terms"} body={doc.contract_terms} />
        <Block title={lang === "es" ? "Órdenes de cambio" : "Change orders"} body={doc.change_order_language} />
        <Block title={lang === "es" ? "Responsabilidades del cliente" : "Customer responsibilities"} body={doc.customer_responsibilities} />
        <Block title={lang === "es" ? "Exclusiones" : "Exclusions"} body={doc.exclusions} />
        <div>
          <div className="overline mb-1">{lang === "es" ? "Precio del contrato" : "Contract price"}</div>
          <div className="font-mono-jc text-2xl font-bold">{money(totals.total)}</div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">
            {lang === "es" ? "Depósito a pagar:" : "Deposit due:"} {money(totals.deposit)} · {lang === "es" ? "Saldo:" : "Balance:"} {money(totals.balance_due)}
          </div>
          {allowanceTotal > 0 && (
            <div data-testid="contract-allowance-summary" className="text-xs text-[var(--jc-text-soft)] mt-2">
              <span className="font-semibold">{D.allowance_total_label}:</span> {money(allowanceTotal)}
            </div>
          )}
          {tmEstimate > 0 && (
            <div data-testid="contract-tm-summary" className="text-xs text-[var(--jc-text-soft)] mt-1 italic">
              {D.tm_reserve_label}: {money(tmEstimate)} — {D.tm_reserve_note}
            </div>
          )}
        </div>
      </section>

      {(snap.line_items || []).some(isTm) && (
        <section data-testid="contract-tm-section" className="mt-5 overflow-x-auto">
          <div className="overline mb-2">{D.tm_section_title}</div>
          <table className="w-full text-sm border border-[var(--jc-border)]">
            <thead className="bg-[var(--jc-bg)]"><tr><th className="text-left px-3 py-2">{D.item}</th><th className="text-right px-3 py-2">{D.qty}</th><th className="text-right px-3 py-2">{D.est_total}</th></tr></thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {(snap.line_items || []).filter(isTm).map((l, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{l.name || "—"}</div>
                    <div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td>
                  <td className="px-3 py-2 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">{D.tm_legend}</p>
        </section>
      )}

      <footer className="mt-6 pt-5 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)] leading-relaxed">
        <div className="overline mb-1">Legal disclaimer · Aviso legal</div>
        <p className="whitespace-pre-line">{doc.legal_disclaimer}</p>
      </footer>
    </article>
  );
};

const CustomerPreview = ({ open, onOpenChange, kind, doc }) => {
  const { t } = useLang();
  if (!doc) return null;
  const lang = doc.language || doc.snapshot?.language || "en";

  const copyAsText = async () => {
    const text = toPlainText(kind, doc);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("customer_preview.toast_copied"));
    } catch {
      window.prompt(t("customer_preview.prompt_copy"), text);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`${kind}-customer-preview`}
        className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-0 gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-[var(--jc-border)] bg-[var(--jc-bg)] sticky top-0 z-10">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Eye size={16} className="text-[var(--jc-blue)]" />
            {kind === "proposal" ? t("customer_preview.title_proposal") : t("customer_preview.title_contract")}
            <span
              data-testid={`${kind}-customer-preview-lang`}
              className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-[var(--jc-border)] text-[var(--jc-text)]"
            >
              {lang.toUpperCase()}
            </span>
            <button
              type="button"
              onClick={copyAsText}
              data-testid={`${kind}-customer-preview-copy-btn`}
              className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--jc-border-strong)] bg-white text-xs font-semibold hover:bg-[var(--jc-bg)]"
              title={t("customer_preview.copy_title")}
            >
              <Copy size={12} /> {t("customer_preview.copy_btn")}
            </button>
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-[var(--jc-text-soft)]">
              <ShieldCheck size={12} className="text-[var(--jc-teal)]" />
              {t("customer_preview.readonly_notice")}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("customer_preview.sr_dialog_desc", kind)}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 lg:p-6 bg-[var(--jc-bg)]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-3 text-xs text-[var(--jc-text-soft)]">
              <Logo variant="mark" theme="dark" size="xs" />
              <span>{t("customer_preview.secure_link_note")}</span>
            </div>
            {kind === "proposal" ? <ProposalPreview doc={doc} /> : <ContractPreview doc={doc} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerPreview;
